import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Computer as ComputerIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import dashboardApiService from '../../services/dashboard/DashboardApiService';

const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    // Handle URL parameters for filtering
    const filterParam = searchParams.get('filter');
    if (filterParam) {
      switch (filterParam) {
        case 'active':
          setStatusFilter('active');
          break;
        case 'inactive':
        case 'disconnected':
          setStatusFilter('disconnected');
          break;
        case 'total':
        case 'all':
          setStatusFilter('all');
          break;
        default:
          setStatusFilter('all');
      }
    }
  }, [searchParams]);

  useEffect(() => {
    filterAgents();
  }, [agents, searchTerm, statusFilter, agentFilter]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      const agentsData = await dashboardApiService.getAllAgents();
      setAgents(agentsData);
    } catch (err) {
      setError('Failed to fetch agents data');
      console.error('Agents fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterAgents = () => {
    let filtered = agents;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(agent =>
        agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.ip.includes(searchTerm) ||
        agent.id.includes(searchTerm) ||
        agent.os.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(agent => agent.status === statusFilter);
    }

    // Filter by specific agent
    if (agentFilter !== 'all') {
      filtered = filtered.filter(agent => agent.id === agentFilter);
    }

    setFilteredAgents(filtered);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'disconnected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="status-icon-active" />;
      case 'disconnected':
        return <CancelIcon className="status-icon-disconnected" />;
      default:
        return <InfoIcon className="status-icon-default" />;
    }
  };

  const formatLastKeepAlive = (lastKeepAlive) => {
    if (lastKeepAlive === '9999-12-31T23:59:59+00:00') {
      return 'Never disconnected';
    }
    return new Date(lastKeepAlive).toLocaleString();
  };

  const handleAgentSelect = (agent) => {
    navigate(`/agents/${agent.id}`);
  };

  const getOSIcon = (osName) => {
    return <ComputerIcon className="os-icon" />;
  };

  if (loading) {
    return (
      <Box className="agents-loading">
        <CircularProgress size={40} />
        <Typography variant="body1" className="loading-text">Loading agents...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="agents-error">
        <Alert severity="error" className="error-alert">
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={fetchAgents}
          startIcon={<RefreshIcon />}
          className="retry-button"
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box className="agents">
      <Box className="agents-header">
        <Typography variant="h4" className="agents-title">
          Agents Management
        </Typography>
        <Typography variant="body1" className="agents-subtitle">
          Monitor and manage all OpenShield agents
        </Typography>
      </Box>

      {/* Filters and Controls */}
      <Box className="agents-controls">
        <Box className="agents-filters">
          <FormControl className="filter-dropdown">
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status Filter"
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="disconnected">Disconnected</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl className="filter-dropdown">
            <InputLabel>Agent Filter</InputLabel>
            <Select
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              label="Agent Filter"
            >
              <MenuItem value="all">All Agents</MenuItem>
              {agents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.name} (ID: {agent.id})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            placeholder="Search agents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="agents-search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        <Box className="agents-stats">
          <Chip 
            label={`Total: ${agents.length}`} 
            className="stat-chip total"
          />
          <Chip 
            label={`Active: ${agents.filter(a => a.status === 'active').length}`} 
            className="stat-chip active"
          />
          <Chip 
            label={`Disconnected: ${agents.filter(a => a.status === 'disconnected').length}`} 
            className="stat-chip disconnected"
          />
          <Chip 
            label={`Filtered: ${filteredAgents.length}`} 
            className="stat-chip filtered"
          />
        </Box>
      </Box>

      {/* Agents Table View */}
      <TableContainer component={Paper} className="agents-table-container">
        <Table className="agents-table">
          <TableHead>
            <TableRow className="table-header">
              <TableCell className="table-header-cell">Agent Name</TableCell>
              <TableCell className="table-header-cell">Status</TableCell>
              <TableCell className="table-header-cell">IP Address</TableCell>
              <TableCell className="table-header-cell">Operating System</TableCell>
              <TableCell className="table-header-cell">Version</TableCell>
              <TableCell className="table-header-cell">Last Keep Alive</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAgents.map((agent) => (
              <TableRow 
                key={agent.id}
                className="table-row"
                onClick={() => handleAgentSelect(agent)}
              >
                <TableCell className="table-cell agent-name-cell">
                  <Box className="agent-name-container">
                    <Avatar className="table-agent-avatar">
                      {getOSIcon(agent.os.name)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" className="table-agent-name">
                        {agent.name}
                      </Typography>
                      <Typography variant="caption" className="table-agent-id">
                        ID: {agent.id}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell className="table-cell">
                  <Box className="status-container">
                    {getStatusIcon(agent.status)}
                    <Chip 
                      label={agent.status}
                      color={getStatusColor(agent.status)}
                      size="small"
                      className="table-status-chip"
                    />
                  </Box>
                </TableCell>
                <TableCell className="table-cell">
                  <Typography variant="body2" className="table-ip">
                    {agent.ip}
                  </Typography>
                  <Typography variant="caption" className="table-register-ip">
                    Register: {agent.registerIP}
                  </Typography>
                </TableCell>
                <TableCell className="table-cell">
                  <Typography variant="body2" className="table-os">
                    {agent.os.name} {agent.os.version}
                  </Typography>
                  <Typography variant="caption" className="table-platform">
                    {agent.os.platform} ({agent.os.arch})
                  </Typography>
                </TableCell>
                <TableCell className="table-cell">
                  <Typography variant="body2" className="table-version">
                    {agent.version}
                  </Typography>
                  <Typography variant="caption" className="table-manager">
                    Manager: {agent.manager}
                  </Typography>
                </TableCell>
                <TableCell className="table-cell">
                  <Typography variant="body2" className="table-last-seen">
                    {formatLastKeepAlive(agent.lastKeepAlive)}
                  </Typography>
                  {agent.disconnection_time && (
                    <Typography variant="caption" className="table-disconnection">
                      Disconnected: {new Date(agent.disconnection_time).toLocaleString()}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>


      {filteredAgents.length === 0 && !loading && (
        <Box className="no-agents">
          <Typography variant="h6">No agents found</Typography>
          <Typography variant="body2">
            {searchTerm ? 'Try adjusting your search terms' : 'No agents are currently registered'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Agents;
